// Shared helpers for the Restaurant Menu feature (editor + public renderer).
import { BACKEND_URL } from "@/lib/api";

export const uid = (p = "id") => `${p}_${Math.random().toString(36).slice(2, 10)}`;

export const CURRENCIES = [
  { code: "AED", symbol: "د.إ" }, { code: "SAR", symbol: "ر.س" }, { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "\u20ac" }, { code: "GBP", symbol: "\u00a3" }, { code: "INR", symbol: "\u20b9" },
  { code: "EGP", symbol: "E\u00a3" }, { code: "QAR", symbol: "ر.ق" },
];
export const currencySymbol = (code) => (CURRENCIES.find((c) => c.code === code) || {}).symbol || code || "";

export const LABELS = {
  special: { name: "Today's Special", ar: "طبق اليوم", color: "#E4572E", icon: "Star" },
  bestseller: { name: "Bestseller", ar: "الأكثر مبيعاً", color: "#F0A500", icon: "Flame" },
  vegetarian: { name: "Vegetarian", ar: "نباتي", color: "#0E8A5F", icon: "Leaf" },
  vegan: { name: "Vegan", ar: "نباتي صرف", color: "#1B9C4B", icon: "Sprout" },
  spicy: { name: "Spicy", ar: "حار", color: "#D7263D", icon: "Flame" },
  new: { name: "New", ar: "جديد", color: "#7A5CFF", icon: "Sparkles" },
};
export const LABEL_IDS = Object.keys(LABELS);

export const TEMPLATES = [
  { id: "classic", name: "Classic", theme: { primary: "#E4572E", bg: "#FFF8F2", card: "#FFFFFF", text: "#1C140D", font: "sans", layout: "list", imageStyle: "rounded", badgeStyle: "soft", buttonStyle: "rounded" } },
  { id: "modern", name: "Modern", theme: { primary: "#0E8A5F", bg: "#F3FBF6", card: "#FFFFFF", text: "#0B1F17", font: "sans", layout: "grid", imageStyle: "rounded", badgeStyle: "solid", buttonStyle: "pill" } },
  { id: "elegant", name: "Elegant", theme: { primary: "#8A6D3B", bg: "#FBF7F0", card: "#FFFFFF", text: "#211A12", font: "serif", layout: "list", imageStyle: "square", badgeStyle: "outline", buttonStyle: "square" } },
  { id: "bold", name: "Bold", theme: { primary: "#F0A500", bg: "#14151A", card: "#1E2027", text: "#F5F5F5", font: "sans", layout: "grid", imageStyle: "rounded", badgeStyle: "solid", buttonStyle: "pill" } },
  { id: "minimal", name: "Minimal", theme: { primary: "#111827", bg: "#FFFFFF", card: "#FFFFFF", text: "#111827", font: "mono", layout: "list", imageStyle: "square", badgeStyle: "outline", buttonStyle: "square" } },
];
export const templateTheme = (id) => (TEMPLATES.find((t) => t.id === id) || TEMPLATES[0]).theme;

export const FONTS = {
  sans: "Inter, ui-sans-serif, system-ui, sans-serif",
  serif: "'Playfair Display', Georgia, 'Times New Roman', serif",
  rounded: "'Nunito', ui-rounded, 'Segoe UI', sans-serif",
  mono: "ui-monospace, 'SFMono-Regular', Menlo, monospace",
};
export const fontStack = (id) => FONTS[id] || FONTS.sans;

export const IMG_RADIUS = { rounded: "0.9rem", square: "0", circle: "9999px" };
export const BTN_RADIUS = { rounded: "0.75rem", pill: "9999px", square: "0" };

export const newItem = (over = {}) => ({
  id: uid("itm"), name: "", description: "", ai_description: false, image: "", image_ai: false,
  price: "", sale_price: "", available: true, labels: [], featured: false, ...over,
});
export const newCategory = (over = {}) => ({ id: uid("cat"), name: "New category", image: "", hidden: false, items: [], ...over });

export const DEFAULT_THEME = { ...templateTheme("classic") };

// Ensure a menu "data" object has the full shape. Legacy menus stored as
// { title, sections:[{name, items:[{name,price,desc}]}] } are migrated to the
// new categories shape (kept fully backward compatible).
export function normalizeMenu(data = {}) {
  const d = { ...data };
  d.restaurant = { name: "", logo: "", tagline: "", phone: "", address: "", hours: "", currency: "AED", ...(d.restaurant || {}) };
  if (!d.restaurant.name && d.title) d.restaurant.name = d.title;
  d.socials = Array.isArray(d.socials) ? d.socials : [];
  d.banners = Array.isArray(d.banners) ? d.banners : [];
  d.language = d.language || "en";
  d.rtl = d.language === "ar" ? true : !!d.rtl;
  d.template = d.template || "classic";
  d.theme = { ...templateTheme(d.template), ...(d.theme || {}) };
  if (!Array.isArray(d.categories)) {
    if (Array.isArray(d.sections) && d.sections.length) {
      d.categories = d.sections.map((s) => newCategory({
        name: s.name || "Menu",
        items: (s.items || []).map((it) => newItem({
          name: it.name || "", price: it.price != null ? String(it.price) : "",
          description: it.desc || it.description || "", available: it.available !== false,
        })),
      }));
    } else {
      d.categories = [];
    }
  }
  d.categories = d.categories.map((c) => ({
    ...newCategory(), ...c,
    items: (c.items || []).map((it) => ({ ...newItem(), ...it })),
  }));
  d.tables = Array.isArray(d.tables) ? d.tables : [];
  return d;
}

export const priceNum = (v) => {
  const n = parseFloat(String(v == null ? "" : v).replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 0 : n;
};
export const effectivePrice = (it) => {
  const sale = priceNum(it.sale_price);
  const reg = priceNum(it.price);
  return sale > 0 && sale < reg ? sale : reg;
};
export const fmtMoney = (v, sym) => {
  const n = priceNum(v);
  const s = Number.isInteger(n) ? String(n) : n.toFixed(2);
  return `${sym ? sym + " " : ""}${s}`;
};

export const menuTableURL = (publicId, table) =>
  `${BACKEND_URL}/api/r/${publicId}${table ? `?t=${encodeURIComponent(table)}` : ""}`;

// i18n
export const T = {
  en: { menu: "Menu", add: "Add", added: "Added", cart: "Your order", empty: "Your selection is empty", total: "Total",
    showWaiter: "Show this order to your waiter", note: "Note for the kitchen (optional)", itemNote: "Add a note",
    soldout: "Sold out", table: "Table", browse: "Continue browsing", review: "Review order", qty: "Qty",
    from: "from", off: "OFF", featured: "Featured", callWaiter: "Show to waiter", items: "items",
    orderSummary: "Order summary", waiterHint: "Your waiter will confirm this and add it to the bill." },
  ar: { menu: "القائمة", add: "أضف", added: "تمت الإضافة", cart: "طلبك", empty: "لم تختر أي طبق بعد", total: "الإجمالي",
    showWaiter: "اعرض هذا الطلب على النادل", note: "ملاحظة للمطبخ (اختياري)", itemNote: "أضف ملاحظة",
    soldout: "غير متوفر", table: "طاولة", browse: "متابعة التصفح", review: "مراجعة الطلب", qty: "الكمية",
    from: "من", off: "خصم", featured: "مميز", callWaiter: "اعرض على النادل", items: "أصناف",
    orderSummary: "ملخص الطلب", waiterHint: "سيؤكد النادل الطلب ويضيفه إلى الفاتورة." },
};
export const tr = (lang, k) => (T[lang] || T.en)[k] || T.en[k] || k;
